import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Download, ExternalLink, Sparkles, QrCode } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_helios-platform-2/artifacts/zblm1o4j_Multimix%20Energy%20Pvt%20Ltd%20Logo%20%28150%20x%2050%20mm%29%20%281%29.png";

export default function PublicProposalView() {
  const { token } = useParams();
  const [prop, setProp] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${BASE}/api/public/proposals/${token}`)
      .then((r) => setProp(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Proposal not found"));
  }, [token]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-8">
      <div className="max-w-md border border-[#0a0a0a] bg-white p-8 text-center">
        <div className="label-eyebrow !text-[10px] mb-2 text-[#DC2626]">Error</div>
        <div className="font-black text-2xl tracking-tight mb-2">Link expired</div>
        <div className="text-sm text-[#4B5563]">{err}</div>
      </div>
    </div>
  );
  if (!prop) return <div className="min-h-screen flex items-center justify-center"><div className="label-eyebrow">Loading…</div></div>;

  const c = prop.customer || {};
  const opts = [prop.option1, prop.option2].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="public-proposal-page">
      {/* Header */}
      <div className="bg-white border-b border-[#0a0a0a] px-6 py-4 flex items-center gap-3 sticky top-0 z-10">
        <img src={LOGO_URL} alt="Multimix Energy" className="h-8 w-auto" />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow !text-[9px]">Solar Proposal · v{prop.version}</div>
          <div className="font-bold text-sm tracking-tight truncate">For {c.name}</div>
        </div>
        <a
          href={`${BASE}/api/public/proposals/${token}/pdf`}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 border border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-9 px-3 hover:bg-[#0a0a0a] hover:text-white"
          data-testid="public-download-pdf"
        >
          <Download className="w-3 h-3" /> Download PDF
        </a>
      </div>

      <div className="max-w-5xl mx-auto p-4 sm:p-8 space-y-6">
        {/* Customer */}
        <div className="bg-white border border-[#E5E7EB] p-6">
          <div className="label-eyebrow mb-3">Customer Profile</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Field label="Name" value={c.name} />
            <Field label="Phone" value={c.phone} />
            <Field label="Consumer No." value={c.consumer_number} />
            <Field label="Monthly Consumption" value={`${c.monthly_consumption_kwh || 0} kWh`} />
            <Field label="Monthly Bill" value={inr(c.monthly_bill_inr)} />
            <Field label="Bi-Monthly Consumption" value={`${c.bi_monthly_consumption_kwh || 0} kWh`} />
            <Field label="Bi-Monthly Bill" value={inr(c.bi_monthly_bill_inr)} />
            {/* Address gets its own full-width row — addresses tend to be long and
                would otherwise visually overlap the Consumer No. cell. */}
            <div className="col-span-2 sm:col-span-4">
              <Field label="Address" value={c.address} wrap />
            </div>
          </div>
        </div>

        {/* Comparison */}
        {opts.length > 0 && (
          <div className="bg-white border border-[#0a0a0a] overflow-x-auto">
            <div className="bg-[#0a0a0a] text-white px-6 py-3">
              <div className="label-eyebrow !text-[9px] text-white opacity-80">Option Comparison Summary</div>
              <div className="font-black text-lg tracking-tight">Choose the solar solution that suits you</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr>{["", "Solution", "Capacity", "Final Price", "Annual Gen", "Annual Savings", "Payback"].map((h) => (
                  <th key={h} className="text-left label-eyebrow !text-[9px] px-3 py-3">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {opts.map((o, i) => (
                  <tr key={i} className="border-b border-[#F3F4F6]">
                    <td className="px-3 py-2 font-black">Option {i + 1}</td>
                    <td className="px-3 py-2 font-semibold">{o.template?.name}</td>
                    <td className="px-3 py-2 text-xs">{o.template?.capacity_kw} kW · {o.template?.system_type}</td>
                    <td className="px-3 py-2 font-bold">{inr(o.roi?.final_price_inr)}</td>
                    <td className="px-3 py-2 text-xs">{Math.round(o.roi?.annual_generation_kwh || 0).toLocaleString("en-IN")} kWh</td>
                    <td className="px-3 py-2 font-semibold text-[#10B981]">{inr(o.roi?.annual_savings_inr)}</td>
                    <td className="px-3 py-2 text-xs">{o.roi?.payback_years || "—"} yrs</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Per-option panels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {opts.map((o, i) => (
            <div key={i} className="bg-white border border-[#E5E7EB] p-6 space-y-4" data-testid={`public-option-${i + 1}`}>
              <div className="label-eyebrow">Option {i + 1}</div>
              <h2 className="font-black text-2xl tracking-tight">{o.template?.name}</h2>
              <div className="text-sm text-[#4B5563]">{o.template?.capacity_kw} kW · {o.template?.system_type} · {o.template?.package_type}</div>

              {o.ai_content?.recommended_solution && (
                <p className="text-sm leading-relaxed border-l-2 border-[#002FA7] pl-3">{o.ai_content.recommended_solution}</p>
              )}

              <div className="space-y-2 border-t border-[#F3F4F6] pt-3">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow !text-[9px]">Components</div>
                  {prop.show_prices && (
                    <div className="text-[9px] uppercase tracking-widest font-bold text-[#4B5563]">
                      Line Total
                    </div>
                  )}
                </div>
                {(o.template?.items || []).map((it, idx) => {
                  // Same qty fallback as the PDF: prefer numeric `qty_num`
                  // when explicitly set, else parse the free-form `quantity`
                  // ("5", "1 Set"), else default to 1.
                  const qNum = Number(it.qty_num);
                  const qStr = String(it.quantity || "").trim();
                  const parsedStr = parseFloat(qStr);
                  const qty = (qNum && qNum !== 1) ? qNum
                    : (qStr && qStr !== "1" && !isNaN(parsedStr)) ? parsedStr
                    : (qNum || 1);
                  const qtyLabel = (qNum && qNum !== 1) ? qty
                    : (qStr && qStr !== "1") ? qStr
                    : qty;
                  const price = Number(it.price_inr || 0);
                  const lineTotal = qty * price;
                  return (
                    <div key={idx} className="text-sm flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold">{it.name}</div>
                        <div className="text-[11px] text-[#4B5563]">{it.description}</div>
                        {prop.show_prices && price > 0 && (
                          <div className="text-[10px] text-[#9CA3AF] font-mono mt-0.5">
                            {qtyLabel} × {inr(price)}
                          </div>
                        )}
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <div className="text-[11px] text-[#0a0a0a] font-bold">× {qtyLabel}</div>
                        {prop.show_prices && price > 0 && (
                          <div className="text-[12px] font-black text-[#0a0a0a]">{inr(lineTotal)}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {(o.extra_items || []).map((it, idx) => {
                  // Extras follow the same fallback + always show with an
                  // asterisk so the customer can tell them apart from the
                  // template BOM.
                  const qNum = Number(it.qty_num);
                  const qStr = String(it.quantity || "").trim();
                  const parsedStr = parseFloat(qStr);
                  const qty = (qNum && qNum !== 1) ? qNum
                    : (qStr && qStr !== "1" && !isNaN(parsedStr)) ? parsedStr
                    : (qNum || 1);
                  const qtyLabel = (qNum && qNum !== 1) ? qty
                    : (qStr && qStr !== "1") ? qStr
                    : qty;
                  const price = Number(it.price_inr || 0);
                  const lineTotal = qty * price;
                  return (
                    <div key={`extra-${idx}`} className="text-sm flex items-start justify-between gap-2 pl-2 border-l-2 border-[#F59E0B]">
                      <div className="min-w-0">
                        <div className="font-semibold">{it.name} <span className="text-[10px] text-[#F59E0B]">*</span></div>
                        <div className="text-[11px] text-[#4B5563]">{it.description}</div>
                        {prop.show_prices && price > 0 && (
                          <div className="text-[10px] text-[#9CA3AF] font-mono mt-0.5">
                            {qtyLabel} × {inr(price)}
                          </div>
                        )}
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <div className="text-[11px] text-[#0a0a0a] font-bold">× {qtyLabel}</div>
                        {prop.show_prices && price > 0 && (
                          <div className="text-[12px] font-black text-[#0a0a0a]">{inr(lineTotal)}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-[#F3F4F6] pt-3 space-y-1">
                <Line label="Package" value={inr(o.roi?.package_price_inr)} />
                <Line label="Discount" value={`− ${inr(o.roi?.discount_inr)}`} />
                <Line label="Final Price" value={inr(o.roi?.final_price_inr)} bold />
              </div>

              <div className="border-t border-[#F3F4F6] pt-3 grid grid-cols-2 gap-2 text-xs">
                <Stat label="Annual Generation" value={`${Math.round(o.roi?.annual_generation_kwh || 0).toLocaleString("en-IN")} kWh`} />
                <Stat label="Monthly Savings" value={inr(o.roi?.monthly_savings_inr)} />
                <Stat label="Payback" value={`${o.roi?.payback_years || "—"} yrs`} />
                <Stat label="25-yr Savings" value={inr(o.roi?.twentyfive_year_savings_inr)} accent="success" />
              </div>

              {o.ai_content?.financial_benefits && (
                <p className="text-sm leading-relaxed text-[#4B5563]">{o.ai_content.financial_benefits}</p>
              )}
              {o.ai_content?.environmental_benefits && (
                <p className="text-sm leading-relaxed text-[#4B5563] bg-[#F0FDF4] p-3 border-l-2 border-[#10B981]">
                  <Sparkles className="inline w-3 h-3 mr-1 text-[#10B981]" />{o.ai_content.environmental_benefits}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#E5E7EB] p-6">
          <div className="label-eyebrow mb-2">Next Steps</div>
          <p className="text-sm text-[#4B5563]">To confirm your selection or request a site visit, please contact our team. Quote is valid for 30 days.</p>
        </div>

        <PayTokenAdvance proposal={prop} />

        <div className="text-center text-xs text-[#9CA3AF] py-4">
          Generated by Multimix Energy Pvt Ltd · {new Date(prop.created_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}

function PayTokenAdvance({ proposal }) {
  const bd = proposal?.settings_snapshot?.bank_details || {};
  const upi = (bd.upi_id || "").trim();
  if (!upi) return null;
  const amount = Number(proposal?.token_advance_inr || 0);
  const payee = bd.account_holder || "Multimix Energy Pvt Ltd";
  const customerName = proposal?.customer?.name || "Customer";
  const note = `Token Advance · ${customerName} · v${proposal?.version || 1}`;
  const params = new URLSearchParams({ pa: upi, pn: payee, cu: "INR", tn: note });
  if (amount > 0) params.set("am", String(amount.toFixed(2)));
  const upiUrl = `upi://pay?${params.toString()}`;
  const qrPath = (bd.upi_qr_url || "").trim();
  const qrSrc = qrPath ? `${BASE}/api/public/upi-qr?path=${encodeURIComponent(qrPath)}` : null;

  return (
    <div className="bg-white border-2 border-[#10B981] p-6" data-testid="public-pay-token-block">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow !text-[10px] text-[#10B981] mb-2">Reserve Your Slot</div>
          <div className="font-black text-2xl tracking-tight">Pay Token Advance</div>
          <div className="text-sm text-[#4B5563] mt-1">
            Lock your installation date by paying a refundable token of <b>{inr(amount)}</b> to <span className="font-mono">{upi}</span>.
          </div>
          <a
            href={upiUrl}
            className="mt-4 inline-flex items-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white uppercase tracking-widest text-xs font-bold h-11 px-5"
            data-testid="public-pay-token-btn"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Pay {inr(amount)} via UPI
          </a>
          <div className="text-[11px] text-[#9CA3AF] mt-2">
            Opens GPay / PhonePe / Paytm / any UPI app. On desktop, scan the QR on the right with your phone.
          </div>
        </div>
        {qrSrc ? (
          <div className="flex flex-col items-center gap-2">
            <img src={qrSrc} alt="UPI QR" className="w-40 h-40 object-contain border border-[#E5E7EB] bg-white p-2" data-testid="public-upi-qr" />
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563] flex items-center gap-1">
              <QrCode className="w-3 h-3" /> Scan to Pay
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Field({ label, value, wrap }) {
  return (
    <div>
      <div className="label-eyebrow !text-[9px] text-[#9CA3AF]">{label}</div>
      <div
        className={`font-semibold text-[#0a0a0a] ${
          wrap ? "whitespace-normal break-words leading-snug" : ""
        }`}
      >
        {value || "—"}
      </div>
    </div>
  );
}
function Line({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between text-sm ${bold ? "border-t border-[#0a0a0a] pt-1" : ""}`}>
      <span className={bold ? "font-bold" : "text-[#4B5563]"}>{label}</span>
      <span className={bold ? "font-black" : ""}>{value}</span>
    </div>
  );
}
function Stat({ label, value, accent }) {
  const tone = accent === "success" ? "text-[#10B981]" : "text-[#0a0a0a]";
  return (
    <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-2">
      <div className="label-eyebrow !text-[9px] text-[#4B5563]">{label}</div>
      <div className={`font-black text-base ${tone}`}>{value}</div>
    </div>
  );
}
